import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from "react";
import { useDispatch } from "react-redux";

import {
  FACILITY_DEFAULT_FILTER_NAME,
  SYSTEM_DEFAULT_FILTERS,
  SYSTEM_DEFAULT_FILTER_NAME
} from "constants/filter.constants";
import { dequal } from "dequal";
import { FilterState } from "store/features/filter/filterSlice";
import { useFilterLandingBinStore } from "stores/filterLandingBinStore";
import { setGlobalGroupBy } from "store/features/groupBy/groupBySlice";
import { updateSelectedProjectTransaction } from "store/features/project/projectSlice";

import { ActiveFilterItem, FilterItemT, LegendItemModel, SavedFilterModel } from "models";
import { mBinSize } from "models/binSize";

import { useUserDefaults } from "components/user/hooks";

import { useSavedFiltersQuery } from "../queries";
import { GroupByFilterDependencies, UseSavedFiltersControllerResult } from "../types";
import { checkedValueCount } from "../util/checkedBins";
import { getStoredPerformanceDateRangeFromSession } from "../util/getStoredPerformanceDateRangeFromSession";
import { setStoredPerformanceDateRangeToSession } from "../util/setStoredPerformanceDateRangeToSession";
import { UseSessionBinStorageResult } from "./useSessionBinStorage";

export interface UseSavedFiltersControllerProps {
  deps: GroupByFilterDependencies;
  selectedProjectId: Maybe<string>;
  session: UseSessionBinStorageResult;
  checkedLegendItemsRef: MutableRefObject<LegendItemModel[]>;
  defaultFiltersLoaded: boolean;
  setDefaultFiltersLoaded: Dispatch<SetStateAction<boolean>>;
  readyForDefaultFilter: boolean;
  setReadyForDefaultFilter: Dispatch<SetStateAction<boolean>>;
  applyRestoredBin: (
    property: string,
    binSettings: mBinSize,
    checkedCount: number
  ) => void;
  canEditFilters: boolean;
}

function getActiveFilterItem(): FilterItemT[] {
  return [ActiveFilterItem];
}

// EVA-6412 SR-004b (NEW, T030) — owns saved-filter load/select/default-resolution
// + the modified indicator, lifted verbatim out of GroupByFilterActivity.tsx.
// Consumes useSavedFiltersQuery + deps — never entityKind directly (fixes P2).
// Uses deps.systemFilterCount to collapse the Well/Facility system-filter-count
// branches (research.md R9) into single formulas.
export const useSavedFiltersController = ({
  deps,
  selectedProjectId,
  session,
  checkedLegendItemsRef,
  defaultFiltersLoaded,
  setDefaultFiltersLoaded,
  readyForDefaultFilter,
  setReadyForDefaultFilter,
  applyRestoredBin,
  canEditFilters
}: UseSavedFiltersControllerProps): UseSavedFiltersControllerResult => {
  const dispatch = useDispatch();
  const defaults = useUserDefaults();
  const { localPerformanceDateRangeRef, updateSessionStorage } = session;

  const {
    isWell,
    systemFilterCount,
    savedFilters,
    selectedFilter,
    excludePolygonsFilter,
    wellListFilter,
    propertiesFilter,
    polygonFilter,
    groupBy,
    setSavedEntityFilters,
    setEntityFiltersLoaded,
    setSelectedFilter: setSelectedFilterInDeps,
    setFilterState,
    setViewLock
  } = deps;

  const isFilterModified = (() => {
    if (!selectedFilter || !selectedFilter.filterJSON) {
      return false;
    }
    if (!canEditFilters) {
      return false;
    }
    try {
      const savedFilterState = JSON.parse(selectedFilter.filterJSON);
      // Reconstruct the current filter snapshot the SAME way the original did
      // (and the same way a filter is serialized when saved): the polygon is
      // only part of the comparison when it has a `.source`. Comparing the raw
      // polygonFilter directly made an unmodified filter report "modified"
      // (saved JSON has no polygon key -> undefined, vs the live null default).
      const currentFilterState = {
        ...(polygonFilter?.source && { polygonFilter }),
        excludePolygonsFilter,
        wellListFilter,
        propertiesFilter
      };
      return (
        !dequal(
          savedFilterState.excludePolygonsFilter ?? [],
          currentFilterState.excludePolygonsFilter ?? []
        ) ||
        !dequal(
          savedFilterState.wellListFilter ?? [],
          currentFilterState.wellListFilter ?? []
        ) ||
        !dequal(
          savedFilterState.propertiesFilter ?? [],
          currentFilterState.propertiesFilter ?? []
        ) ||
        !dequal(savedFilterState.polygonFilter, currentFilterState.polygonFilter)
      );
    } catch {
      return false;
    }
  })();

  const isSystemDefaultFilterSelected =
    selectedFilter && SYSTEM_DEFAULT_FILTERS.includes(selectedFilter.name.toLowerCase());

  const updateBinSettingsFromSavedFilter = useCallback(
    (filter: {
      propertiesFilter?: { predicates?: { property?: string; bin?: unknown }[] }[];
    }) => {
      if (!filter.propertiesFilter || filter.propertiesFilter.length === 0) {
        return;
      }
      let currentPropertyBinUpdated = false;
      for (const propertyFilter of filter.propertiesFilter) {
        if (propertyFilter.predicates) {
          for (const predicate of propertyFilter.predicates) {
            if (predicate.property && predicate.bin) {
              const savedBin = predicate.bin as Partial<mBinSize>;
              const binSettings: mBinSize = {
                BinSize: savedBin.BinSize || "",
                LessThan: savedBin.LessThan || null,
                GreaterThan: savedBin.GreaterThan || null,
                MinSize: savedBin.MinSize || "",
                MaxBins: savedBin.MaxBins || "",
                MaxBinsSortOrder: savedBin.MaxBinsSortOrder || "WellCount",
                MaxBinsSortField: savedBin.MaxBinsSortField,
                BinType: savedBin.BinType || "BinSize",
                Quantile: savedBin.Quantile || undefined,
                IsLocked: savedBin.IsLocked || false,
                UseDynamicBins: savedBin.UseDynamicBins || false
              };
              updateSessionStorage(predicate.property, binSettings);
              if (predicate.property === groupBy.property && !currentPropertyBinUpdated) {
                applyRestoredBin(
                  predicate.property,
                  binSettings,
                  checkedValueCount(
                    filter.propertiesFilter as FilterState["propertiesFilter"],
                    predicate.property
                  )
                );
                currentPropertyBinUpdated = true;
              }
            }
          }
        }
      }
    },
    [updateSessionStorage, groupBy.property, applyRestoredBin]
  );

  const updatePerformanceDateRangeFromSavedFilter = useCallback(
    (filter: {
      propertiesFilter?: {
        predicates?: { property?: string; performanceDateRange?: unknown }[];
      }[];
    }) => {
      if (!filter.propertiesFilter || filter.propertiesFilter.length === 0) {
        return;
      }
      const restoredDateRanges: Record<string, { startDate: string; endDate: string }> =
        {};
      for (const propertyFilter of filter.propertiesFilter) {
        if (propertyFilter.predicates) {
          for (const predicate of propertyFilter.predicates) {
            if (predicate.property && predicate.performanceDateRange) {
              restoredDateRanges[predicate.property] = predicate.performanceDateRange as {
                startDate: string;
                endDate: string;
              };
            }
          }
        }
      }
      if (Object.keys(restoredDateRanges).length === 0) {
        return;
      }
      const merged = {
        ...getStoredPerformanceDateRangeFromSession(),
        ...restoredDateRanges
      };
      localPerformanceDateRangeRef.current = merged;
      setStoredPerformanceDateRangeToSession(merged);

      const currentPropertyDateRange = restoredDateRanges[groupBy.property];
      if (currentPropertyDateRange) {
        dispatch(
          setGlobalGroupBy({ ...groupBy, performanceDateRange: currentPropertyDateRange })
        );
      }
    },
    [localPerformanceDateRangeRef, groupBy, dispatch]
  );

  const onSelectedFilterChanged = useCallback(
    (selected: Maybe<SavedFilterModel>) => {
      setSelectedFilterInDeps(selected);
      if (!selected || !selected.filterJSON) {
        return;
      }
      const filter = JSON.parse(selected.filterJSON);
      setViewLock(!!filter.polygonFilter);
      // The legend keeps its rows until this filter lands, so none of its values tick the
      // old bins; the checked-bins flag goes out with it too (landWellFilterId).
      if (isWell) useFilterLandingBinStore.getState().setAwaitingFilter(true);
      setFilterState(filter);
      checkedLegendItemsRef.current = filter.checkedItems;
      updateBinSettingsFromSavedFilter(filter);
      updatePerformanceDateRangeFromSavedFilter(filter);
    },
    [
      isWell,
      setSelectedFilterInDeps,
      setViewLock,
      setFilterState,
      checkedLegendItemsRef,
      updateBinSettingsFromSavedFilter,
      updatePerformanceDateRangeFromSavedFilter
    ]
  );

  // when the filter is modified and it's a system default filter, switch to active filter
  useEffect(() => {
    if (isFilterModified && isSystemDefaultFilterSelected && savedFilters.length > 0) {
      onSelectedFilterChanged(savedFilters[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterModified, isSystemDefaultFilterSelected, savedFilters]);

  const { refetch: refetchSavedFilters } = useSavedFiltersQuery(
    selectedProjectId,
    groupBy.entityKind
  );

  const updateSavedFilters = useCallback(async (): Promise<
    Maybe<{ savedFilters: SavedFilterModel[] }>
  > => {
    try {
      const result = (await refetchSavedFilters()) as { data?: unknown };
      const data = result.data as Maybe<{ savedFilters: SavedFilterModel[] }>;
      if (!data || data.savedFilters.length === 0) {
        setSavedEntityFilters(getActiveFilterItem() as unknown as SavedFilterModel[]);
        return data;
      }
      const filters = (getActiveFilterItem() as unknown as SavedFilterModel[]).concat(
        data.savedFilters
      );
      setSavedEntityFilters(filters);
      setEntityFiltersLoaded(true);
      const hasOnlySystemFilters = isWell
        ? data.savedFilters.length === systemFilterCount
        : data.savedFilters.every((filter) => filter.name === "Facility Default");
      const filterProjectId =
        data.savedFilters.length > systemFilterCount
          ? data.savedFilters[systemFilterCount].projectID
          : null;
      if (
        !defaults?.project ||
        hasOnlySystemFilters ||
        selectedProjectId === filterProjectId
      ) {
        setReadyForDefaultFilter(true);
      }
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refetchSavedFilters,
    isWell,
    systemFilterCount,
    defaults?.project,
    selectedProjectId,
    setSavedEntityFilters,
    setEntityFiltersLoaded,
    setReadyForDefaultFilter
  ]);

  useEffect(() => {
    if (!readyForDefaultFilter || defaultFiltersLoaded) {
      return;
    }

    const defaultFilter = isWell ? defaults?.filter : defaults?.facilityFilter;
    // The list is [Active Filter, ...backend filters], so index 1 was the
    // entity default only while the backend returned it first, which nothing
    // asserts. Found by name now, with the old index kept as the fallback.
    //
    // This 1 was never systemFilterCount: that counts backend system filters
    // and indexes the list before Active Filter is prepended. The two only
    // ever coincided by accident, which is why they read as one idea.
    const entityDefaultName = isWell
      ? SYSTEM_DEFAULT_FILTER_NAME
      : FACILITY_DEFAULT_FILTER_NAME;
    const systemDefaultFilter =
      savedFilters.find((f) => f?.name?.toLowerCase() === entityDefaultName) ??
      savedFilters[1];
    if (defaultFilter) {
      let match = savedFilters.find(
        (x) => x.id === defaultFilter.filterId && !!defaultFilter.filterId
      );
      if (!match && savedFilters.length > systemFilterCount) {
        match = systemDefaultFilter;
      } else if (!match && savedFilters.length > 0) {
        match = savedFilters[0];
      }
      onSelectedFilterChanged(match);
    } else if (savedFilters.length > systemFilterCount) {
      onSelectedFilterChanged(systemDefaultFilter);
    }
    setDefaultFiltersLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyForDefaultFilter, defaultFiltersLoaded, isWell]);

  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;
  useEffect(() => {
    if (selectedProjectId) {
      updateSavedFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, deps.selectedProjectTransaction, groupBy.entityKind]);

  // EVA-6412 P1 thin-controller follow-up (moved from useGroupByFilterController):
  // only ever touched this hook's own updateSavedFilters/onSelectedFilterChanged +
  // a project-transaction reset dispatch — an obvious home here, not in the controller.
  const onSaveFilter = useCallback(
    async (filterName: string) => {
      const updatedData = await updateSavedFilters();
      dispatch(updateSelectedProjectTransaction(""));
      if (filterName && updatedData?.savedFilters) {
        const newlyCreatedFilter = updatedData.savedFilters.find(
          (f) => f.name === filterName
        );
        if (newlyCreatedFilter) {
          onSelectedFilterChanged(newlyCreatedFilter);
        }
      }
    },
    [updateSavedFilters, dispatch, onSelectedFilterChanged]
  );

  return {
    savedFilters,
    selectedFilter,
    isFilterModified,
    isSystemDefaultFilterSelected: !!isSystemDefaultFilterSelected,
    onSelectedFilterChanged,
    updateSavedFilters,
    onSaveFilter
  };
};
